import type { Client } from '@optimizely/optimizely-sdk';
import { createInstance, enums } from '@optimizely/optimizely-sdk';

type ConfigDatafile = object | string;

type ClientLogger = {
	error: (args: { error: Error; message: string; scope: string }) => void;
	warn: (args: { message: string; scope: string }) => void;
};

type ClientConfig = {
	get: (key: 'optimizely_sdk_key' | 'optimizely_url') => unknown;
	logger: ClientLogger;
};

type ClientState = {
	client: Client | null;
	getConfig: ClientConfig['get'];
	initialisationPromise: Promise<Client | null> | null;
	isClientReady: boolean;
	logger: ClientLogger;
	readyPromise: Promise<void> | null;
};

const SCOPE = 'serverOptimizely';
const READY_TIMEOUT_MS = 5_000;
const DATAFILE_UPDATE_INTERVAL_MS = 60_000;
const DEFAULT_OPTIMIZELY_URL = 'https://cdn.optimizely.com';
const STATE_KEY = Symbol.for('motorway-dealer-webapp.server-optimizely-client');

const defaultLogger: ClientLogger = {
	error: () => undefined,
	warn: () => undefined,
};

const createState = (): ClientState => ({
	client: null,
	getConfig: (key) => {
		if (key === 'optimizely_sdk_key') return process.env.OPTIMIZELY_SDK || '';

		return process.env.OPTIMIZELY_URL || DEFAULT_OPTIMIZELY_URL;
	},
	initialisationPromise: null,
	isClientReady: false,
	logger: defaultLogger,
	readyPromise: null,
});

const globalStore = globalThis as unknown as Record<symbol, ClientState | undefined>;
const state = globalStore[STATE_KEY] ?? createState();
globalStore[STATE_KEY] = state;

const convertToError = (error: unknown): Error => (error instanceof Error ? error : new Error(String(error)));

export const configureServerOptimizelyClient = ({ get, logger }: ClientConfig): void => {
	state.getConfig = get;
	state.logger = logger;
};

export const getServerOptimizelyClient = (datafile?: ConfigDatafile): Promise<Client | null> => {
	if (state.client) return Promise.resolve(state.client);
	if (state.initialisationPromise) return state.initialisationPromise;

	state.initialisationPromise = (async () => {
		const sdkKey = state.getConfig('optimizely_sdk_key');

		if (!sdkKey) {
			state.logger.warn({
				message: 'Optimizely SDK key not configured — server-side feature flags disabled',
				scope: SCOPE,
			});
			return null;
		}

		const optimizelyUrl = state.getConfig('optimizely_url');
		const urlTemplate = typeof optimizelyUrl === 'string' ? `${optimizelyUrl}/datafiles/%s.json` : undefined;
		const instance = createInstance({
			...(datafile ? { datafile } : {}),
			datafileOptions: {
				autoUpdate: true,
				updateInterval: DATAFILE_UPDATE_INTERVAL_MS,
				...(urlTemplate && { urlTemplate }),
			},
			logLevel: enums.LOG_LEVEL.ERROR,
			odpOptions: { disabled: true },
			sdkKey: String(sdkKey),
		});

		if (!instance) {
			throw new Error('Failed to create Optimizely client instance');
		}

		state.client = instance;

		if (datafile) {
			state.isClientReady = true;
		}

		instance
			.onReady({ timeout: READY_TIMEOUT_MS })
			.then(({ reason, success }) => {
				if (!success && !datafile) {
					state.logger.warn({
						message: `Optimizely client not ready within ${READY_TIMEOUT_MS}ms: ${reason ?? 'unknown'}`,
						scope: SCOPE,
					});
				}
			})
			.catch((error: unknown) => {
				state.logger.error({
					error: convertToError(error),
					message: 'Optimizely client readiness check failed',
					scope: SCOPE,
				});
			});

		const longLivedReady = instance
			.onReady()
			.then(({ success }) => {
				if (success && state.client === instance) {
					state.isClientReady = true;
				}
			})
			.catch((error: unknown) => {
				state.logger.error({
					error: convertToError(error),
					message: 'Optimizely long-lived readiness check failed',
					scope: SCOPE,
				});
			});

		if (!datafile) {
			state.readyPromise = longLivedReady;
		}

		return state.client;
	})().catch((error: unknown) => {
		state.logger.error({
			error: convertToError(error),
			message: 'Optimizely client initialisation failed',
			scope: SCOPE,
		});
		state.initialisationPromise = null;
		return null;
	});

	return state.initialisationPromise;
};

export const getServerOptimizelyReadiness = (): {
	isClientReady: boolean;
	readyPromise: Promise<void> | null;
} => ({ isClientReady: state.isClientReady, readyPromise: state.readyPromise });

/** Reset the singleton — only for use in tests. @internal */
// eslint-disable-next-line no-underscore-dangle
export const _resetServerOptimizelyClientForTesting = (): void => {
	if (state.client) {
		state.client.close().catch(() => {
			// Swallow errors during test teardown
		});
	}
	state.client = null;
	state.initialisationPromise = null;
	state.isClientReady = false;
	state.readyPromise = null;
};
